import { createWSApi, type WSClientLike } from '@thinknimble/tn-models'
import { AsyncJob, JobEvent, jobEventShape, jobStatusEnum, JobStatusValues } from './models'

const TERMINAL_STATUSES: JobStatusValues[] = [
  jobStatusEnum.DONE,
  jobStatusEnum.FAILED,
  jobStatusEnum.CANCELLED,
]

export function isTerminalJobStatus(status: unknown): status is JobStatusValues {
  return TERMINAL_STATUSES.includes(status as JobStatusValues)
}

/**
 * Hydrate the ws-broadcast subset ({@link JobEvent}) into a full {@link AsyncJob}.
 * The broadcast carries the summary plus the failure detail (`errorMessage`/
 * `failureReason`) and the task's `result` (e.g. receipt-analysis expense data);
 * the remaining progress fields it omits get neutral defaults. The broadcast
 * payload is self-sufficient, so the client never needs a `retrieve()`.
 */
function jobEventToAsyncJob(e: JobEvent): AsyncJob {
  return {
    id: e.id,
    status: e.status,
    taskType: e.taskType,
    entityId: e.entityId,
    label: e.label,
    entityType: '',
    phase: '',
    progressCurrent: 0,
    progressTotal: 0,
    errorMessage: e.errorMessage,
    failureReason: e.failureReason,
    result: e.result ?? null,
    startedAt: null,
    completedAt: null,
    created: '',
  }
}

const WS_CHANNEL = 'jobs'
const WS_EVENT = 'event'

/**
 * The `ws/jobs/` consumer, built on the tn-models ws consumer ({@link createWSApi}).
 *
 * `createWSApi` owns the wire-format transform: every incoming payload is camelCased
 * and validated against {@link jobEventShape} before it reaches `onJob` — so the
 * client no longer hand-parses keys. Our server sends the job summary as the raw
 * message body (no `{event, data}` envelope), so instead of `createWSAdapter` we feed
 * a minimal {@link WSClientLike} directly: `feed(raw)` dispatches one message through
 * the consumer. Transport (reconnect/backoff) stays with the caller's socket.
 */
export function createJobsWsConsumer(onJob: (job: AsyncJob) => void) {
  const handlers = new Set<(data: unknown) => void>()
  const eventName = `${WS_CHANNEL}:${WS_EVENT}`

  const client: WSClientLike = {
    send: () => {},
    on: (event, handler) => {
      if (event === eventName) handlers.add(handler)
    },
    off: (event, handler) => {
      if (event === eventName && handler) handlers.delete(handler)
    },
  }

  const api = createWSApi({
    channel: WS_CHANNEL,
    client,
    operations: { receive: { [WS_EVENT]: { outputShape: jobEventShape } } },
  })

  api.on[WS_EVENT]((event: JobEvent) => {
    if (!event || typeof event.id !== 'string' || !event.id) return
    onJob(jobEventToAsyncJob(event))
  })

  return {
    /** Push one raw `ws/jobs/` message through the tn-models transform + onJob. */
    feed: (raw: unknown) => {
      handlers.forEach((handler) => handler(raw))
    },
  }
}

export type JobEventProcessorDeps = {
  pushRecent: (job: AsyncJob) => void
  notifyJobTerminal: (job: AsyncJob) => void
  invalidateForJob: (job: AsyncJob) => void
  removeFromActive: (id: string) => void
  replaceActive: (jobs: AsyncJob[]) => void
  retrieve: (id: string) => Promise<AsyncJob>
  listActive: () => Promise<AsyncJob[]>
  /** Ids the store currently believes are active — includes optimistic inserts
   * the server may not have surfaced yet. The reconcile diffs against this so a
   * job that left the server's active set is caught even if its ws event dropped. */
  getKnownActiveIds: () => string[]
}

/**
 * Shared terminal-job orchestration for the websocket and the fallback poll.
 *
 * Both paths funnel terminal jobs through {@link processTerminal}, which keeps
 * a per-session set of already-handled ids so a job pushed over the socket and
 * later observed by a reconcile fetch toasts exactly once. The ws path feeds the
 * transformed event straight to {@link processTerminal} (see
 * {@link createJobsWsConsumer}); the reconcile/poll path detects jobs that left the
 * active set and fetches their final state ({@link reconcile}/{@link ingestActive}).
 */
export function createJobEventProcessor(getDeps: () => JobEventProcessorDeps) {
  const processed = new Set<string>()

  function processTerminal(job: AsyncJob): boolean {
    if (!isTerminalJobStatus(job.status)) return false
    if (processed.has(job.id)) return false
    processed.add(job.id)

    const deps = getDeps()
    deps.pushRecent(job)
    deps.notifyJobTerminal(job)
    deps.invalidateForJob(job)
    deps.removeFromActive(job.id)
    return true
  }

  /**
   * Diff a fresh server active list against what the store currently believes
   * is active; any id that left the set went terminal, so fetch its final state
   * and process it. Diffing against the store (not an internal snapshot) means
   * optimistically-inserted jobs are reconciled too — even if their websocket
   * terminal event was dropped. Shared by the reconnect reconcile and the poll.
   */
  function ingestActive(activeJobs: AsyncJob[]) {
    const newIds = new Set(activeJobs.map((j) => j.id))
    const terminalIds = getDeps()
      .getKnownActiveIds()
      .filter((id) => !newIds.has(id) && !processed.has(id))

    getDeps().replaceActive(activeJobs)

    for (const id of terminalIds) {
      getDeps()
        .retrieve(id)
        .then((job) => processTerminal(job))
        .catch(() => {
          // Job may have been deleted or access lost — silently ignore.
        })
    }
  }

  /** One reconcile fetch on (re)connect, catching completions during a gap. */
  async function reconcile() {
    const active = await getDeps().listActive()
    ingestActive(active)
  }

  return { ingestActive, reconcile, processTerminal }
}

export type JobEventProcessor = ReturnType<typeof createJobEventProcessor>
